import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { ArrowLeft, CheckCircle2, Circle, Trophy, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * MasteryBuilder — calm, structured skill-building dashboard for PDIs and ADIs.
 *
 * Tone: "Slow is control. Master one layer before adding another.
 *        We are not going backwards — we are mastering the skill properly."
 */
export default function MasteryBuilder() {
  const [data, setData] = useState(null);
  const nav = useNavigate();

  const goBack = useSafeBack();
  useEffect(() => {
    axios.get(`${API}/api/mastery/skills`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData({ skills: [], totals: { mastered: 0, total: 0 }, principle: "" }));
  }, []);

  if (!data) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <p className="text-slate-400">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 pb-20">
      <header className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button
            data-testid="mastery-back-btn"
            onClick={() => goBack()}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
          <div>
            <h1 className="text-base font-extrabold text-slate-900 dark:text-white">Mastery Builder</h1>
            <p className="text-[11px] text-slate-500">Build every skill properly · {data.totals.mastered}/{data.totals.total} mastered</p>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {/* Principle banner */}
        <div className="rounded-2xl bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/10 border border-indigo-100 dark:border-indigo-900/40 p-4">
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 mt-0.5 text-indigo-600 dark:text-indigo-300" />
            <p className="text-sm text-indigo-900 dark:text-indigo-100 leading-snug">
              <span className="font-extrabold">Slow is control.</span> Master one layer before adding another.
              <br />
              <span className="text-indigo-700 dark:text-indigo-300 text-xs">
                We are not going backwards — we are mastering the skill properly.
              </span>
            </p>
          </div>
        </div>

        {/* Skills list */}
        <ul data-testid="mastery-skills-list" className="space-y-2">
          {data.skills.map((s) => {
            const pct = s.total_layers > 0 ? Math.round((s.layers_done.length / s.total_layers) * 100) : 0;
            const mastered = s.status === "mastered";
            const started = s.status === "in_progress";
            return (
              <li key={s.id}>
                <button
                  data-testid={`mastery-skill-${s.id}`}
                  onClick={() => nav(`/instructor/mastery/${s.id}`)}
                  className={`w-full text-left rounded-2xl border p-3 transition-all active:scale-[0.99] ${
                    mastered
                      ? "bg-emerald-50 dark:bg-emerald-900/15 border-emerald-200 dark:border-emerald-900/40"
                      : started
                      ? "bg-white dark:bg-slate-800 border-blue-200 dark:border-blue-900/40"
                      : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      mastered ? "bg-emerald-600 text-white" : started ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" : "bg-slate-100 text-slate-400 dark:bg-slate-700 dark:text-slate-500"
                    }`}>
                      {mastered ? <Trophy className="w-4 h-4" /> : started ? <Circle className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-extrabold text-slate-900 dark:text-white">{s.title}</p>
                        {mastered && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-600 text-white">Mastered</span>
                        )}
                        {started && !mastered && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">In progress</span>
                        )}
                      </div>
                      <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">{s.summary}</p>
                      {/* Progress bar */}
                      {s.total_layers > 0 && (
                        <div className="mt-2 h-1 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${mastered ? "bg-emerald-500" : "bg-blue-500"}`}
                            style={{ width: `${mastered ? 100 : pct}%` }}
                          />
                        </div>
                      )}
                      <p className="mt-1 text-[10px] text-slate-400">
                        {s.layers_done.length}/{s.total_layers} layers
                      </p>
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
